/**
 * <revenue-breakdown> — the dashboard money cell's answer sheet: how the
 * headline figure is worked out, in the four lines
 * the § 14 invoice's own arithmetic already produces. Gross taken, then the
 * two deductions each carrying its leading −, then the answer set apart by a
 * divider and its own type scale — no "=" glyph and no colour on it, because
 * the sheet IS the equation and the answer is a reading, not a verdict.
 *
 * Every row keeps its definition sentence in every state; the fee row's
 * bound qualifier is a line beneath that definition, never a replacement for
 * it and never prose in the money column, whose right edge is what
 * tabular-nums exists to hold. With no report in yet the fee figure is an em
 * dash — a € there would read as a fact nobody has reported — and the answer
 * row is suppressed whole, because a you-keep figure that pretends the PSP
 * was free is the one thing this sheet must never print. With a partial
 * report the answer is an upper bound, fees only growing as reports land, and
 * the footer alone carries how many are outstanding, since only the footer
 * states the consequence.
 *
 * Orders whose refunds could not be attributed to lines are excluded from
 * every figure upstream (the same refusal the correction PDF makes), so when
 * any were excluded the sheet says how many rather than letting its total
 * disagree silently with the order list beside it.
 *
 * Reads `consoleViewModel` directly, mounted bare with zero properties, like
 * every overlay this console opens over its own state.
 *
 * Also carries its own named close button, alongside the normal
 * click-outside / Escape dismissal every overlay gets.
 */

import { formatCurrency } from "@mhmo91/rx";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, type TemplateResult } from "lit";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { CURRENCY, consoleViewModel } from "./console.state.ts";

@schmancyElement("revenue-breakdown")
export class RevenueBreakdown extends LocalizeMixin(SchmancyElement) {
	override render(): TemplateResult {
		const vm = consoleViewModel.get();
		if (!vm) return html``;

		const pending = vm.youKeepCents === null;
		const partial =
			!pending &&
			vm.feeOrdersTotal > 0 &&
			vm.feeOrdersReported < vm.feeOrdersTotal;
		// A qualifier beside the fee row's definition, never instead of it: the
		// state where an operator most needs to know what a transaction fee is
		// is the state where none has been reported.
		const feeQualifier = pending
			? this.t("admin.events.console.revenueBreakdown.feesNotReported")
			: partial
				? this.t("admin.events.console.revenueBreakdown.feesAtLeast")
				: null;
		// Non-null on every path this reads it — the answer row renders only
		// when a fee report is in — but the view model types it nullable for
		// the branch above, which the template's own arrow does not narrow.
		const youKeep = formatCurrency(
			(vm.youKeepCents ?? 0) / 100,
			CURRENCY,
			this.locale,
		);

		return html`
			<div class="flex w-md max-w-full flex-col gap-4 p-6">
				<div class="flex items-start justify-between gap-6">
					<div class="flex min-w-0 flex-col">
						<schmancy-typography type="body" token="md">${this.t("admin.events.console.revenueBreakdown.grossLabel")}</schmancy-typography>
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
							${this.t("admin.events.console.revenueBreakdown.grossExplained")}
						</schmancy-typography>
					</div>
					<schmancy-typography type="body" token="md" class="shrink-0 tabular-nums">${formatCurrency(vm.grossTakenCents / 100, CURRENCY, this.locale)}</schmancy-typography>
				</div>

				<div class="flex items-start justify-between gap-6">
					<div class="flex min-w-0 flex-col">
						<schmancy-typography type="body" token="md">${this.t("admin.events.console.revenueBreakdown.vatLabel")}</schmancy-typography>
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
							${
								vm.governingVatRate !== null
									? this.t(
											"admin.events.console.revenueBreakdown.vatSingleRate",
											{ rate: vm.governingVatRate },
										)
									: this.t("admin.events.console.revenueBreakdown.vatMixedRate")
							}
						</schmancy-typography>
					</div>
					<schmancy-typography type="body" token="md" class="shrink-0 tabular-nums">− ${formatCurrency(vm.vatOwedCents / 100, CURRENCY, this.locale)}</schmancy-typography>
				</div>

				<div class="flex items-start justify-between gap-6">
					<div class="flex min-w-0 flex-col">
						<schmancy-typography type="body" token="md">${this.t("admin.events.console.revenueBreakdown.feesLabel")}</schmancy-typography>
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
							${this.t("admin.events.console.revenueBreakdown.feesExplained")}
						</schmancy-typography>
						${when(
							feeQualifier !== null,
							() =>
								html`<schmancy-typography type="body" token="sm" class="text-surface-onVariant">${feeQualifier}</schmancy-typography>`,
						)}
					</div>
					${when(
						pending,
						// An em dash holds the money column open without asserting a
						// figure the provider has not reported.
						() =>
							html`<schmancy-typography type="body" token="md" class="shrink-0 tabular-nums" aria-hidden="true">—</schmancy-typography>`,
						() =>
							html`<schmancy-typography type="body" token="md" class="shrink-0 tabular-nums">− ${formatCurrency(vm.feeReportedCents / 100, CURRENCY, this.locale)}</schmancy-typography>`,
					)}
				</div>

				${when(
					!pending,
					() => html`
						<schmancy-divider class="my-2"></schmancy-divider>
						<div class="flex items-start justify-between gap-6">
							<schmancy-typography type="title" token="lg" weight="bold">${this.t("admin.events.console.revenueBreakdown.keepLabel")}</schmancy-typography>
							<schmancy-typography type="title" token="lg" weight="bold" class="shrink-0 tabular-nums">
								${
									partial
										? this.t(
												"admin.events.console.revenueBreakdown.keepAtMost",
												{ amount: youKeep },
											)
										: youKeep
								}
							</schmancy-typography>
						</div>
						${when(
							partial,
							() => html`
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
									${this.t(
										"admin.events.console.revenueBreakdown.keepPending",
										{ count: vm.feeOrdersTotal - vm.feeOrdersReported },
									)}
								</schmancy-typography>
							`,
						)}
					`,
				)}

				${when(
					vm.unattributableOrders > 0,
					() => html`
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
							${this.t(
								"admin.events.console.revenueBreakdown.unattributableNote",
								{ count: vm.unattributableOrders },
							)}
						</schmancy-typography>
					`,
				)}

				<div class="flex justify-end">
					<schmancy-button
						variant="text"
						@click=${() =>
							this.dispatchEvent(
								new CustomEvent("close", { bubbles: true, composed: true }),
							)}
					>${this.t("admin.common.close")}</schmancy-button>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"revenue-breakdown": RevenueBreakdown;
	}
}
